import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useSearchParams } from 'react-router-dom';
import { RefreshCw } from 'lucide-react';
import { DetailBreadcrumb } from '../../../../components/ui/DetailBreadcrumb';
import { DetailHero } from '../../../../components/ui/DetailHero';
import { FilterBar } from '../../../../components/ui/FilterBar';
import { SearchInput } from '../../../../components/ui/SearchInput';
import { FilterableSelect } from '../../../../components/ui/FilterableSelect';
import { CountChips } from '../../../../components/ui/CountChips';
import { DataTable } from '../../../../components/ui/DataTable';
import { Switch } from '../../../../components/ui/Switch';
import { PendingButton } from '../../../../components/ui/PendingButton';
import { ToneBadge } from '../../../../components/ui/ToneBadge';
import { ErrorState } from '../../../../components/ui/ErrorState';
import { useNotice } from '../../../../hooks/useNotice';
import { selectIsRunning } from '../../../../features/feedback/feedbackSlice';
import {
  setListingsFilters,
  stockSyncChannelSelected,
  stockSyncLeft,
  stockSyncWatched,
} from '../../../../features/stockSync/stockSyncSlice';
import {
  fetchListings,
  fetchStockSync,
  fetchStockSyncChannels,
  syncListing,
  updateListing,
} from '../../../../features/stockSync/stockSyncThunks';
import { selectChannelOptions, selectUsableChannels } from '../../../../features/stockSync/stockSyncSelectors';
import { LISTING_FILTER, STOCK_SYNC_WATCHER } from '../../../../features/stockSync/stockSyncConstants';
import {
  formatSyncDate,
  listingFilterOptions,
  listingStateOf,
  pickDefaultChannelId,
  pushBlocker,
  shopDomain,
} from '../../../../features/stockSync/stockSyncUtils';
import { selectCurrentWarehouse } from '../../../../features/warehouseContext/warehouseContextSlice';
import { formatQuantity } from '../../../../utils/inventoryLabels';

const INVENTORY_PATH = '/warehouse/inventory';

/** Pauses one product, or resumes it, from its row. Spins alone while its change is out. */
const ListingSwitch = ({ row, onChange }) => {
  const saving = useSelector(selectIsRunning(updateListing, row.id));
  return (
    <Switch
      checked={row.sync_enabled}
      onChange={(syncEnabled) => onChange(row, syncEnabled)}
      loading={saving}
      disabled={Boolean(row.blocked_reason)}
      ariaLabel={`Publicar ${row.product.sku} en la tienda`}
      testId="shopify-listing-publish"
    />
  );
};

/**
 * `/warehouse/inventory/shopify?store=<id>`: the products of a Shopify store
 * linked to the catalog, and how each one stands with it — what Ninja
 * publishes now, what the store had last time, the last error. One can be
 * paused, or pushed again by hand. The store comes in the address; without
 * it, the page picks it as the dialogs do.
 */
export const InventoryShopify = () => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const storeParam = Number(searchParams.get('store')) || null;

  const warehouse = useSelector(selectCurrentWarehouse);
  const channels = useSelector(selectUsableChannels);
  const channelOptions = useSelector(selectChannelOptions);
  const { channelsLoaded, selectedId, detail, detailError, listings } = useSelector((state) => state.stockSync);
  const { rows, totalCount, loading, error, filters } = listings;

  const [searchInput, setSearchInput] = useState(filters.search);
  const setNotice = useNotice('shopify-listings-notice');

  useEffect(() => {
    dispatch(stockSyncWatched(STOCK_SYNC_WATCHER.LISTINGS));
    return () => dispatch(stockSyncLeft(STOCK_SYNC_WATCHER.LISTINGS));
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchStockSyncChannels({ warehouseId: warehouse?.id ?? null }));
  }, [dispatch, warehouse?.id]);

  // The store in the address is the one in use; without one, the page picks it.
  useEffect(() => {
    if (storeParam) {
      dispatch(stockSyncChannelSelected(storeParam));
      return;
    }
    if (!channelsLoaded) return;
    const settingId = pickDefaultChannelId(channels, { selectedId });
    if (settingId) setSearchParams({ store: String(settingId) }, { replace: true });
  }, [dispatch, storeParam, channelsLoaded, channels, selectedId, setSearchParams]);

  useEffect(() => {
    if (selectedId) dispatch(fetchStockSync({ settingId: selectedId }));
  }, [dispatch, selectedId]);

  // Debounce for search
  useEffect(() => {
    const handler = setTimeout(() => {
      const search = searchInput.trim();
      if (search !== filters.search) dispatch(setListingsFilters({ search }));
    }, 500);
    return () => clearTimeout(handler);
  }, [searchInput, filters.search, dispatch]);

  useEffect(() => {
    if (selectedId) dispatch(fetchListings());
  }, [dispatch, selectedId, filters.search, filters.status, filters.page, filters.rowsPerPage]);

  const toggleListing = async (row, syncEnabled) => {
    const action = await dispatch(updateListing({ settingId: selectedId, listingId: row.id, syncEnabled }));
    if (updateListing.rejected.match(action)) setNotice({ type: 'error', message: action.payload });
  };

  const pushListing = async (row) => {
    const action = await dispatch(syncListing({ settingId: selectedId, listingId: row.id }));
    if (syncListing.rejected.match(action)) setNotice({ type: 'error', message: action.payload });
  };

  const columns = [
    {
      header: 'PRODUCTO',
      cell: (row) => {
        const state = listingStateOf(row);
        return (
          <div
            className="flex flex-col max-w-[260px]"
            data-testid="shopify-listing-row"
            data-listing-id={row.id}
            data-status={state.key}
          >
            <Link
              to={`${INVENTORY_PATH}/${row.product.id}`}
              className="font-bold font-mono text-ninja-dark dark:text-white hover:text-ninja-green"
              title="Ver los movimientos de este producto"
            >
              {row.product.sku}
            </Link>
            <span className="text-xs text-text-muted mt-0.5 truncate" title={row.product.name}>
              {row.product.name}
            </span>
          </div>
        );
      },
    },
    {
      header: 'EN SHOPIFY',
      cell: (row) => (
        <div className="flex flex-col max-w-[240px]">
          <span className="truncate" title={row.title}>
            {row.title}
          </span>
          {row.external_sku && (
            <span className="text-xs text-text-muted font-mono mt-0.5">SKU {row.external_sku}</span>
          )}
        </div>
      ),
    },
    {
      // What Ninja publishes now, and what it last knew of the store.
      header: 'STOCK',
      cell: (row) => (
        <div
          className="flex flex-col tabular-nums"
          title={
            row.last_set_quantity !== null && row.last_set_quantity !== undefined
              ? `Lo último que Ninja envió: ${formatQuantity(row.last_set_quantity)}`
              : undefined
          }
        >
          <span className="font-semibold">Ninja {formatQuantity(row.ninja_quantity)}</span>
          <span className="text-xs text-text-muted">
            Tienda {row.channel_available === null ? '—' : formatQuantity(row.channel_available)}
          </span>
          {!row.initial_loaded_at && <span className="text-xs text-amber-700 dark:text-amber-300">Sin sincronizar</span>}
        </div>
      ),
    },
    {
      header: 'ESTADO',
      cell: (row) => {
        const state = listingStateOf(row);
        return (
          // The table's cells do not wrap: these texts have to, or they run under the next columns.
          <div className="flex flex-col items-start gap-1 max-w-[320px]">
            <ToneBadge tone={state.tone} testId="shopify-listing-state">
              {state.label}
            </ToneBadge>
            {state.key === 'error' && (
              <span className="text-xs text-danger whitespace-normal line-clamp-2" title={row.last_sync_error}>
                {row.last_sync_error}
              </span>
            )}
            {/* Not clamped: it ends on what to do. */}
            {state.key === 'blocked' && (
              <span className="text-xs text-amber-700 dark:text-amber-300 whitespace-normal">
                {row.last_sync_error || row.blocked_reason_label}
              </span>
            )}
            {row.last_synced_at && state.key !== 'error' && state.key !== 'blocked' && (
              <span className="text-xs text-text-muted">{formatSyncDate(row.last_synced_at)}</span>
            )}
          </div>
        );
      },
    },
    {
      header: 'PUBLICAR',
      cell: (row) => <ListingSwitch row={row} onChange={toggleListing} />,
    },
    {
      header: 'ACCIONES',
      cell: (row) => {
        const blocker = pushBlocker(row, detail);
        return (
          <div className="flex items-center justify-end">
            <PendingButton
              pendingOn={syncListing}
              pendingKey={row.id}
              variant="subtle"
              size="sm"
              iconOnly
              icon={RefreshCw}
              disabled={Boolean(blocker)}
              title={blocker || 'Publicar ahora: manda a la tienda lo que Ninja tiene'}
              aria-label={`Publicar ${row.product.sku} ahora`}
              data-testid="shopify-listing-sync"
              onClick={() => pushListing(row)}
            />
          </div>
        );
      },
    },
  ];

  const isFiltering = filters.search !== '' || filters.status !== LISTING_FILTER.ALL;
  const emptyMessage = isFiltering
    ? 'No hay productos que coincidan con estos filtros.'
    : 'Todavía no hay productos vinculados a esta tienda. Sincroniza los SKUs desde Productos.';

  return (
    <div data-testid="shopify-listings-page" className="space-y-6 animate-in fade-in duration-300">
      <DetailBreadcrumb backLabel="Volver a Inventario" to={INVENTORY_PATH} current="Shopify" />

      <DetailHero
        testId="shopify-listings-hero"
        media={<RefreshCw size={36} strokeWidth={1.5} />}
        title="Inventario en Shopify"
        badges={
          detail && (
            <ToneBadge tone={detail.enabled ? 'success' : 'neutral'} testId="shopify-publish-state">
              {detail.enabled ? 'Publicando' : 'Publicación apagada'}
            </ToneBadge>
          )
        }
        meta={
          detail && (
            <>
              <span>{detail.channel.company.name}</span>
              <span>{shopDomain(detail.channel.shop_url)}</span>
              <span>
                {detail.channel.warehouse.name}
                {detail.location ? ` → «${detail.location.name}»` : ' · sin ubicación en la tienda'}
              </span>
            </>
          )
        }
      />

      {detailError && (
        <ErrorState
          variant="inline"
          title="No se pudo leer la tienda"
          description={detailError}
          onRetry={() => dispatch(fetchStockSync({ settingId: selectedId }))}
        />
      )}

      {!selectedId && channelsLoaded && channels.length === 0 && (
        <p className="text-sm text-text-secondary" data-testid="shopify-listings-none">
          Ningún cliente con el WMS en esta bodega tiene su tienda Shopify conectada.
        </p>
      )}

      {detail && (
        <CountChips
          options={listingFilterOptions(detail.listings, filters.status)}
          value={filters.status}
          onChange={(status) => dispatch(setListingsFilters({ status }))}
          allValue={LISTING_FILTER.ALL}
          allLabel="Todos"
          total={detail.listings?.total}
          testId="shopify-listings-status"
        />
      )}

      <FilterBar variant="bare" testId="shopify-listings-filters">
        <SearchInput
          variant="bare"
          value={searchInput}
          onChange={setSearchInput}
          placeholder="Buscar por SKU o nombre..."
          testId="shopify-listings-search"
        />
        {channelOptions.length > 1 && (
          <FilterableSelect
            label="Tienda:"
            value={selectedId ?? ''}
            onChange={(settingId) => setSearchParams({ store: String(settingId) })}
            options={channelOptions}
            placeholder="Elige la tienda"
            searchPlaceholder="Buscar cliente o tienda..."
            panelMinWidth={280}
            testId="shopify-listings-store"
          />
        )}
      </FilterBar>

      {selectedId && (
        <DataTable
          columns={columns}
          data={rows}
          totalItems={totalCount}
          currentPage={filters.page}
          itemsPerPage={filters.rowsPerPage}
          isLoading={loading}
          error={error}
          onRetry={() => dispatch(fetchListings())}
          itemLabel="productos"
          emptyMessage={emptyMessage}
          onPageChange={(page) => dispatch(setListingsFilters({ page }))}
          onRowsPerPageChange={(rowsPerPage) => dispatch(setListingsFilters({ rowsPerPage }))}
        />
      )}

      {!selectedId && channels.length > 1 && (
        <p className="text-sm text-text-secondary" data-testid="shopify-listings-choose">
          Elige arriba la tienda para ver sus productos.
        </p>
      )}
    </div>
  );
};
